import { Fragment } from 'react';
import {
  isKnownQuestionType,
  QuestionTypeEnum,
  FormTemplate,
} from '@employ-engage/data';
import { useSurveyResponseUtils } from '../survey-response-utils/survey-response-utils';
import EERadioGroup from '../eeradio-group/eeradio-group';
import EECheckboxGroup from '../eecheckbox-group/eecheckbox-group';
import EETextArea from '../eetext-area/eetext-area';
import { useTranslation } from 'react-i18next';

export interface FormPreviewProps {
  form: FormTemplate;
}

export const FormPreview = ({ form }: FormPreviewProps) => {
  const { t } = useTranslation(['common']);
  const { calculateQuestionNumber } = useSurveyResponseUtils();
  return (
    <div>
      {form?.pages?.map((page, pageIndex) => (
        <div key={`page-${pageIndex}`}>
          {page?.questions?.map((question, questionIndex) => (
            <Fragment key={`question-${questionIndex}`}>
              <h2 className="text-lg font-bold">
                {calculateQuestionNumber(pageIndex, questionIndex, form.pages)}.{' '}
                {question.question}
              </h2>
              <div className="mb-10">
                {question.type === QuestionTypeEnum.SELECT_ONE && (
                  <EERadioGroup
                    previewOnly
                    isColumn={question?.isColumn}
                    options={question?.options}
                  />
                )}
                {question.type === QuestionTypeEnum.SELECT_MANY &&
                  !!question?.options && (
                    <EECheckboxGroup
                      previewOnly
                      options={question?.options}
                      optionLabelClass="text-gray-700"
                    />
                  )}
                {question.type === QuestionTypeEnum.LONG_TEXT && (
                  <EETextArea
                    previewOnly
                    className="sm:mt-10"
                    placeholder={t('Type your answer here...')}
                  />
                )}
                {question.type === QuestionTypeEnum.SHORT_TEXT && (
                  <div>
                    <input
                      type="text"
                      disabled
                      className="rounded-md border-2 border-gray-400 p-2 text-custom-textColor w-full"
                      placeholder={t('Type your answer here...')}
                      autoComplete="off"
                    />
                  </div>
                )}
                {question.type === QuestionTypeEnum.SCALE && (
                  <div className="flex flex-wrap gap-2">
                    {Array.from(
                      {
                        length:
                          (question.scale?.max ?? 5) -
                          (question.scale?.min ?? 1) +
                          1,
                      },
                      (_, i) => (question.scale?.min ?? 1) + i
                    ).map((point) => (
                      <span
                        key={point}
                        className="rounded border-2 border-gray-400 px-4 py-2"
                      >
                        {point}
                      </span>
                    ))}
                  </div>
                )}
                {question.type === QuestionTypeEnum.NUMBER && (
                  <input
                    type="number"
                    disabled
                    className="rounded-md border-2 border-gray-400 p-2 text-custom-textColor"
                  />
                )}
                {question.type === QuestionTypeEnum.DATE && (
                  <input
                    type="date"
                    disabled
                    className="rounded-md border-2 border-gray-400 p-2 text-custom-textColor"
                  />
                )}
                {/* The preview is the last point before a survey goes out, so
                    it is the most useful place to learn that a question will
                    not render — particularly for a template a model wrote. */}
                {!isKnownQuestionType(question.type) && (
                  <p className="rounded bg-semantic-warning/20 p-3 text-sm">
                    {t(
                      'Unsupported question type "{{type}}". This question will ' +
                        'not be shown to anyone — change its type before sending.',
                      { type: String(question.type ?? '') }
                    )}
                  </p>
                )}
              </div>
            </Fragment>
          ))}
        </div>
      ))}
    </div>
  );
};

export default FormPreview;
